import { useState, useEffect, useCallback, useRef } from "react";
import { disruptionAPI } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { toast } from "sonner";
import TeamBuilder from "./TeamBuilder";
import {
  Plus, Lightbulb, Trash2, Loader2, Mic, MicOff, Clock, User, Send,
  GripVertical, MessageSquare, ZoomIn, ZoomOut, Timer, Users, Eye,
  ChevronRight, Settings, Bell, ArrowRight, Sparkles, Layers, Unlink, Pencil,
} from "lucide-react";

// --- Constants ---
const STICKY_COLORS = {
  business_model: { bg: "#EDE9FE", border: "#C4B5FD", text: "#5B21B6", shadow: "rgba(139,92,246,0.15)" },
  product:        { bg: "#FEF3C7", border: "#FCD34D", text: "#92400E", shadow: "rgba(245,158,11,0.15)" },
  service:        { bg: "#D1FAE5", border: "#6EE7B7", text: "#065F46", shadow: "rgba(16,185,129,0.15)" },
  process:        { bg: "#FEE2E2", border: "#FCA5A5", text: "#991B1B", shadow: "rgba(239,68,68,0.15)" },
  region:         { bg: "#DBEAFE", border: "#93C5FD", text: "#1E40AF", shadow: "rgba(59,130,246,0.15)" },
};
const BADGE_COLORS = { business_model: "bg-violet-100 text-violet-700 border-violet-300", product: "bg-amber-100 text-amber-700 border-amber-300", service: "bg-emerald-100 text-emerald-700 border-emerald-300", process: "bg-red-100 text-red-700 border-red-300", region: "bg-blue-100 text-blue-700 border-blue-300" };
const formatTime = (iso) => { if (!iso) return ""; return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); };
const formatDateTime = (iso) => { if (!iso) return ""; const d = new Date(iso); return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); };
const fmtTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const SUB_PHASES = {
  individual: { label: "Individual Brainstorm", icon: User, desc: "Write your ideas privately — 11 min", color: "text-amber-600 bg-amber-50" },
  team_reveal: { label: "Team Discussion", icon: Users, desc: "Discuss as a team — 7 min per person", color: "text-indigo-600 bg-indigo-50" },
  consolidated: { label: "All Teams Board", icon: Eye, desc: "Every team's ideas, one board", color: "text-emerald-600 bg-emerald-50" },
};

// --- Sticky Note ---
const StickyNote = ({ idea, index, userId, onDelete, onSelect, onEdit, onDragEnd, zoom }) => {
  const colors = STICKY_COLORS[idea.category] || STICKY_COLORS.product;
  const [dragging, setDragging] = useState(false);
  const [dragOff, setDragOff] = useState({ x: 0, y: 0 });
  const [pos, setPos] = useState({ x: idea.position?.x ?? (60 + (index % 5) * 230), y: idea.position?.y ?? (20 + Math.floor(index / 5) * 240) });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const rot = useRef(((idea.id?.charCodeAt(0) || 0) % 7) - 3).current;
  const clickTimer = useRef(null);

  const onMD = (e) => {
    if (e.target.closest("[data-action]")) return; // skip action buttons
    e.preventDefault();
    setDragOff({ x: e.clientX / zoom - pos.x, y: e.clientY / zoom - pos.y });
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const mv = (e) => setPos({ x: e.clientX / zoom - dragOff.x, y: e.clientY / zoom - dragOff.y });
    const up = () => { setDragging(false); onDragEnd?.(idea.id, pos); };
    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
  }, [dragging, dragOff, zoom, idea.id, onDragEnd, pos]);

  const onTS = (e) => {
    if (e.target.closest("[data-action]")) return;
    const t = e.touches[0];
    setDragOff({ x: t.clientX / zoom - pos.x, y: t.clientY / zoom - pos.y });
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const mv = (e) => { const t = e.touches[0]; setPos({ x: t.clientX / zoom - dragOff.x, y: t.clientY / zoom - dragOff.y }); };
    const up = () => { setDragging(false); onDragEnd?.(idea.id, pos); };
    window.addEventListener("touchmove", mv, { passive: false });
    window.addEventListener("touchend", up);
    return () => { window.removeEventListener("touchmove", mv); window.removeEventListener("touchend", up); };
  }, [dragging, dragOff, zoom, idea.id, onDragEnd, pos]);

  const handleDoubleClick = (e) => {
    if (e.target.closest("[data-action]")) return;
    onSelect(idea);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    setShowDeleteConfirm(false);
    onDelete(idea.id);
  };

  return (
    <>
      <div data-testid={`sticky-note-${idea.id}`} className="absolute select-none group"
        style={{ left: pos.x, top: pos.y, width: 210, zIndex: dragging ? 100 : showDeleteConfirm ? 90 : 10,
          transform: `rotate(${dragging ? 0 : rot}deg) scale(${dragging ? 1.05 : 1})`,
          transition: dragging ? "none" : "transform 0.2s, box-shadow 0.2s",
          cursor: dragging ? "grabbing" : "grab" }}
        onMouseDown={onMD} onTouchStart={onTS} onDoubleClick={handleDoubleClick}>
        <div className="rounded-sm p-3.5 relative" style={{ backgroundColor: colors.bg, borderLeft: `3px solid ${colors.border}`,
          boxShadow: dragging ? `0 20px 40px ${colors.shadow}, 0 8px 16px rgba(0,0,0,0.1)` : `2px 3px 8px ${colors.shadow}, 1px 2px 4px rgba(0,0,0,0.06)` }}>
          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-40"><GripVertical className="w-3 h-3" style={{ color: colors.text }} /></div>

          {/* Delete button */}
          {idea.submitted_by === userId && (
            <button data-action="delete" data-testid={`delete-note-${idea.id}`}
              onClick={handleDeleteClick}
              className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-80 hover:opacity-100 w-6 h-6 rounded-full flex items-center justify-center bg-white/80 shadow-sm border border-slate-200 z-20 transition-opacity"
              style={{ color: "#EF4444" }}>
              <Trash2 className="w-3 h-3" />
            </button>
          )}

          {/* Edit button */}
          <button data-action="edit" data-testid={`edit-note-${idea.id}`}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onEdit?.(idea); }}
            className="absolute top-1.5 right-6 opacity-0 group-hover:opacity-80 hover:opacity-100 w-6 h-6 rounded-full flex items-center justify-center bg-white/80 shadow-sm border border-slate-200 z-20 transition-opacity"
            style={{ color: "#6366F1" }}>
            <Pencil className="w-3 h-3" />
          </button>

          <span className="inline-block text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mb-1.5" style={{ backgroundColor: colors.border + "40", color: colors.text }}>{idea.category?.replace("_", " ")}</span>
          <h4 style={{ color: colors.text, fontFamily: "'Caveat', cursive", fontSize: "17px" }} className="font-semibold leading-tight mb-1">{idea.title}</h4>
          {idea.description && <p className="line-clamp-3 mb-1.5" style={{ color: colors.text, opacity: 0.7, fontFamily: "'Caveat', cursive", fontSize: "13px", lineHeight: "1.3" }}>{idea.description}</p>}
          {/* Impact & Effort tags */}
          {(idea.impact || idea.effort) && (
            <div className="flex gap-1 mb-1">
              {idea.impact && <span className="text-[7px] font-bold uppercase px-1 py-0.5 rounded" style={{ backgroundColor: colors.border + "30", color: colors.text, opacity: 0.6 }}>{idea.impact} impact</span>}
              {idea.effort && <span className="text-[7px] font-bold uppercase px-1 py-0.5 rounded" style={{ backgroundColor: colors.border + "30", color: colors.text, opacity: 0.6 }}>{idea.effort} effort</span>}
            </div>
          )}
          <div className="flex items-center justify-between mt-1.5 pt-1.5" style={{ borderTop: `1px solid ${colors.border}40` }}>
            <span className="text-[8px] font-medium" style={{ color: colors.text, opacity: 0.5 }}>{idea.submitted_by_name}</span>
            <span className="text-[8px]" style={{ color: colors.text, opacity: 0.35 }}>{formatTime(idea.created_at)}</span>
          </div>
          {(idea.notes?.length || 0) > 0 && <div className="absolute -bottom-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white bg-indigo-500 ring-2 ring-white">{idea.notes.length}</div>}
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-5 max-w-sm mx-4 animate-scale-in" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-dialog">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-base text-slate-900">Delete Idea?</h3>
                <p className="text-xs text-slate-500">This will remove "{idea.title}"</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" data-action="cancel" onClick={() => setShowDeleteConfirm(false)} className="h-8 text-xs">Cancel</Button>
              <Button size="sm" data-action="confirm" data-testid="confirm-delete-btn" onClick={confirmDelete} className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white">
                <Trash2 className="w-3 h-3 mr-1" />Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// --- Team/Member Sidebar ---
const TeamSidebar = ({ groups, ideas, allIdeas, userId, filterGroup, filterUser, setFilterGroup, setFilterUser, subPhase, myGroup }) => {
  const displayGroups = subPhase === "consolidated" ? groups : myGroup ? [myGroup] : groups;
  const ideaCountMap = {};
  (allIdeas || []).forEach((i) => { ideaCountMap[i.submitted_by] = (ideaCountMap[i.submitted_by] || 0) + 1; });

  return (
    <aside className="w-52 bg-white border-r border-slate-200 flex flex-col shrink-0" data-testid="brainstorm-sidebar">
      <div className="px-3 py-2 border-b border-slate-100">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {subPhase === "consolidated" ? "Teams & Members" : subPhase === "team_reveal" ? "Your Team" : "Participants"}
        </span>
      </div>
      <ScrollArea className="flex-1">
        <div className="py-1">
          {subPhase === "consolidated" && (
            <button onClick={() => { setFilterGroup?.(null); setFilterUser?.(null); }}
              className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors ${!filterGroup && !filterUser ? "bg-indigo-50 text-indigo-700 border-r-2 border-indigo-500" : "text-slate-600 hover:bg-slate-50"}`}>
              All Ideas ({allIdeas?.length || 0})
            </button>
          )}
          {displayGroups.map((g) => {
            const gIdeas = (allIdeas || []).filter((i) => i.group_id === g.id);
            return (
              <div key={g.id}>
                {subPhase === "consolidated" && (
                  <button onClick={() => { setFilterGroup?.(g.id); setFilterUser?.(null); }}
                    className={`w-full text-left px-3 py-2 text-xs font-semibold transition-colors ${filterGroup === g.id && !filterUser ? "bg-indigo-50 text-indigo-700 border-r-2 border-indigo-500" : "text-slate-700 hover:bg-slate-50"}`}>
                    {g.name}
                    <Badge variant="outline" className="ml-1.5 text-[9px] font-mono">{gIdeas.length}</Badge>
                  </button>
                )}
                {subPhase !== "consolidated" && (
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-300">{g.name}</div>
                )}
                {(g.members || []).map((m) => {
                  const count = ideaCountMap[m.user_id] || 0;
                  const isMe = m.user_id === userId;
                  return (
                    <button key={m.user_id}
                      onClick={() => { setFilterGroup?.(null); setFilterUser?.(subPhase === "consolidated" ? m.user_id : null); }}
                      className={`w-full text-left px-3 py-1.5 ${subPhase === "consolidated" ? "pl-6" : "pl-4"} text-[11px] transition-colors flex items-center gap-2 ${
                        filterUser === m.user_id ? "bg-indigo-50 text-indigo-700" : "text-slate-500 hover:bg-slate-50"
                      }`}>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0 ${isMe ? "bg-indigo-500" : "bg-slate-400"}`}>
                        {(m.name || "?")[0].toUpperCase()}
                      </div>
                      <span className={`truncate flex-1 ${isMe ? "font-semibold" : ""}`}>{m.name}{isMe ? " (you)" : ""}</span>
                      <span className="font-mono text-[10px] text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5 min-w-[20px] text-center">{count}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </aside>
  );
};

// --- Consolidation Countdown Banner ---
const ConsolidationBanner = ({ seconds, onComplete }) => {
  const [secs, setSecs] = useState(seconds);
  useEffect(() => {
    if (secs <= 0) { onComplete?.(); return; }
    const i = setInterval(() => setSecs((s) => s - 1), 1000);
    return () => clearInterval(i);
  }, [secs, onComplete]);
  return (
    <div className="bg-indigo-600 text-white px-4 py-2.5 flex items-center justify-center gap-3 animate-fade-in" data-testid="consolidation-banner">
      <Bell className="w-4 h-4 animate-pulse" />
      <span className="text-sm font-medium">All teams joining in</span>
      <span className="font-mono text-lg font-bold">{fmtTimer(secs)}</span>
    </div>
  );
};

// --- Main Component ---
const BrainstormView = ({ boardId, session, userId, onSessionRefresh }) => {
  const [ideas, setIdeas] = useState([]);
  const [allIdeas, setAllIdeas] = useState([]); // for sidebar counts
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showTeamBuilder, setShowTeamBuilder] = useState(false);
  const [newIdea, setNewIdea] = useState({ title: "", description: "", category: "product", impact: "", effort: "" });
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [editingIdea, setEditingIdea] = useState(null); // idea being edited inline
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [timerSecs, setTimerSecs] = useState(11 * 60);
  const [timerRunning, setTimerRunning] = useState(false);
  const [filterGroup, setFilterGroup] = useState(null);
  const [filterUser, setFilterUser] = useState(null);
  const [consolidationCountdown, setConsolidationCountdown] = useState(null); // seconds or null
  const [showCountdownSetup, setShowCountdownSetup] = useState(false);
  const [countdownMinutes, setCountdownMinutes] = useState(5);
  const [clusters, setClusters] = useState([]);
  const [clustering, setClustering] = useState(false);
  const [showCluster, setShowCluster] = useState(null);
  const [merging, setMerging] = useState(false);
  const [dragMergeIds, setDragMergeIds] = useState(new Set());
  const [mergeSummary, setMergeSummary] = useState(null); // {title, description, id, merged_from} shown after merge
  const canvasRef = useRef(null);
  const recognitionRef = useRef(null);
  const saveTimerRef = useRef(null);

  const subPhase = session?.brainstorm_sub_phase || "individual";
  const isOwner = session?.created_by === userId;
  const groups = session?.groups || [];
  const myGroup = groups.find((g) => g.members?.some((m) => m.user_id === userId));
  const teamMemberCount = myGroup?.members?.length || 1;

  // Timer logic — default based on phase
  useEffect(() => {
    if (subPhase === "individual") setTimerSecs(11 * 60);
    else if (subPhase === "team_reveal") setTimerSecs(7 * 60 * teamMemberCount);
  }, [subPhase, teamMemberCount]);

  useEffect(() => {
    if (!timerRunning || timerSecs <= 0) return;
    const i = setInterval(() => setTimerSecs((s) => { if (s <= 1) { setTimerRunning(false); toast.info("Time's up!"); return 0; } return s - 1; }), 1000);
    return () => clearInterval(i);
  }, [timerRunning, timerSecs]);

  const descRef = useRef("");
  const toggleVoice = () => {
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) { toast.error("Voice not supported. Try Chrome."); return; }
    if (recording) { recognitionRef.current?.stop(); setRecording(false); return; }
    try {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const r = new SR();
      r.continuous = true;
      r.interimResults = true;
      r.lang = "en-US";
      descRef.current = newIdea.description || "";
      r.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) {
            descRef.current += (descRef.current ? " " : "") + e.results[i][0].transcript;
          } else {
            interim += e.results[i][0].transcript;
          }
        }
        setNewIdea((p) => ({ ...p, description: descRef.current + (interim ? " " + interim : "") }));
      };
      r.onerror = (event) => {
        setRecording(false);
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          toast.error("Microphone access denied. Check browser permissions.");
        } else if (event.error === "aborted") {
          // Silently handle abort
        } else {
          toast.error("Voice error — try again");
        }
      };
      r.onend = () => setRecording(false);
      recognitionRef.current = r;
      r.start();
      setRecording(true);
    } catch (err) {
      toast.error("Could not start voice recognition. Try closing and re-opening the dialog.");
      setRecording(false);
    }
  };

  const fetchIdeas = useCallback(async () => {
    try {
      const groupId = (subPhase === "individual" || subPhase === "team_reveal") ? myGroup?.id : null;
      const res = await disruptionAPI.getIdeas(boardId, groupId);
      const fetched = res.data.ideas || [];
      setIdeas(fetched);
      // Also fetch all for sidebar counts
      if (subPhase !== "consolidated") {
        setAllIdeas(fetched);
      } else {
        const allRes = await disruptionAPI.getIdeas(boardId);
        setAllIdeas(allRes.data.ideas || []);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [boardId, myGroup?.id, subPhase]);

  useEffect(() => { fetchIdeas(); }, [fetchIdeas]);

  const submitIdea = async () => {
    if (!newIdea.title.trim()) { toast.error("Title required"); return; }
    setSubmitting(true);
    try {
      const res = await disruptionAPI.submitIdea(boardId, newIdea);
      setIdeas((prev) => [...prev, res.data]);
      setAllIdeas((prev) => [...prev, res.data]);
      setNewIdea({ title: "", description: "", category: "product", impact: "", effort: "" });
      setShowAdd(false);
      toast.success("Idea added!");
    } catch { toast.error("Failed"); } finally { setSubmitting(false); }
  };

  const deleteIdea = async (id) => { try { await disruptionAPI.deleteIdea(boardId, id); setIdeas((p) => p.filter((i) => i.id !== id)); setAllIdeas((p) => p.filter((i) => i.id !== id)); toast.success("Removed"); } catch { toast.error("Failed"); } };

  const startEdit = (idea) => {
    setEditingIdea(idea);
    setEditForm({ title: idea.title, description: idea.description || "", category: idea.category || "product", impact: idea.impact || "", effort: idea.effort || "" });
  };

  const saveEdit = async () => {
    if (!editingIdea || !editForm.title.trim()) return;
    try {
      await disruptionAPI.updateIdea(boardId, editingIdea.id, { ...editingIdea, title: editForm.title, description: editForm.description, category: editForm.category, impact: editForm.impact, effort: editForm.effort });
      setIdeas((p) => p.map((i) => i.id === editingIdea.id ? { ...i, title: editForm.title, description: editForm.description, category: editForm.category, impact: editForm.impact, effort: editForm.effort } : i));
      setAllIdeas((p) => p.map((i) => i.id === editingIdea.id ? { ...i, title: editForm.title, description: editForm.description, category: editForm.category, impact: editForm.impact, effort: editForm.effort } : i));
      setEditingIdea(null);
      toast.success("Updated");
    } catch { toast.error("Failed to save"); }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selectedIdea) return;
    setAddingNote(true);
    try {
      const res = await disruptionAPI.addNote(boardId, selectedIdea.id, noteText);
      const updated = { ...selectedIdea, notes: [...(selectedIdea.notes || []), res.data] };
      setIdeas((p) => p.map((i) => i.id === selectedIdea.id ? { ...i, notes: updated.notes } : i));
      setSelectedIdea(updated); setNoteText(""); toast.success("Note added");
    } catch { toast.error("Failed"); } finally { setAddingNote(false); }
  };

  const handleDragEnd = useCallback((ideaId, position) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => { try { await disruptionAPI.savePositions(boardId, { [ideaId]: position }); } catch {} }, 500);
  }, [boardId]);

  const advanceSubPhase = async (phase) => {
    try {
      await disruptionAPI.advanceBrainstormPhase(boardId, phase);
      toast.success(`Advanced to ${SUB_PHASES[phase]?.label}`);
      if (onSessionRefresh) await onSessionRefresh();
      fetchIdeas();
    } catch { toast.error("Failed"); }
  };

  const runAIClustering = async () => {
    setClustering(true);
    try {
      const res = await disruptionAPI.clusterIdeas(boardId);
      setClusters(res.data.clusters || []);
      toast.success(`Found ${res.data.clusters?.length || 0} clusters (${res.data.total_clustered} ideas grouped)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clustering failed");
    } finally { setClustering(false); }
  };

  const mergeSelectedIdeas = async (ideaIds) => {
    if (ideaIds.length < 2) { toast.error("Select at least 2 ideas"); return; }
    setMerging(true);
    try {
      const res = await disruptionAPI.mergeIdeas(boardId, ideaIds);
      toast.success("Ideas merged!");
      setShowCluster(null);
      setDragMergeIds(new Set());
      setClusters([]);
      fetchIdeas();
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Merge failed");
    } finally { setMerging(false); }
  };

  const unmergeIdea = async (ideaId) => {
    try {
      await disruptionAPI.unmergeIdea(boardId, ideaId);
      toast.success("Ideas restored!");
      setSelectedIdea(null);
      setClusters([]);
      fetchIdeas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unmerge failed");
    }
  };

  // Build cluster lookup: ideaId -> clusterId
  const ideaClusterMap = {};
  clusters.forEach((c) => c.idea_ids.forEach((id) => { ideaClusterMap[id] = c.id; }));

  const startConsolidationCountdown = () => {
    setConsolidationCountdown(countdownMinutes * 60);
    setShowCountdownSetup(false);
    toast.success(`All-Teams session starts in ${countdownMinutes} minutes`);
  };

  const onConsolidationComplete = useCallback(() => {
    setConsolidationCountdown(null);
    if (isOwner) advanceSubPhase("consolidated");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  // Display filtering — exclude merged originals
  const displayIdeas = (() => {
    let filtered = ideas.filter((i) => !i.is_merged);
    if (subPhase === "individual") filtered = filtered.filter((i) => i.submitted_by === userId);
    else if (subPhase === "consolidated" && filterUser) filtered = filtered.filter((i) => i.submitted_by === filterUser);
    else if (subPhase === "consolidated" && filterGroup) filtered = filtered.filter((i) => i.group_id === filterGroup);
    return filtered;
  })();

  const handleWheel = (e) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); setZoom((z) => Math.min(2, Math.max(0.4, z - e.deltaY * 0.001))); } };
  const spConfig = SUB_PHASES[subPhase]; const SpIcon = spConfig?.icon || User;

  if (loading) return <div className="flex items-center justify-center" style={{ height: "calc(100vh - 200px)" }}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 200px)" }} data-testid="brainstorm-view">
      {/* Consolidation Countdown */}
      {consolidationCountdown !== null && (
        <ConsolidationBanner seconds={consolidationCountdown} onComplete={onConsolidationComplete} />
      )}

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 px-3 py-2 flex items-center justify-between shrink-0 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${spConfig?.color || "bg-amber-50 text-amber-600"}`}><SpIcon className="w-4 h-4" /></div>
          <div>
            <h2 className="font-heading font-bold text-xs text-slate-900">{spConfig?.label}</h2>
            <span className="text-[9px] text-slate-400">{subPhase === "team_reveal" ? `${7 * teamMemberCount} min (${teamMemberCount} members x 7 min)` : spConfig?.desc}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Timer */}
          <div className={`flex items-center gap-1 rounded-lg px-2 py-1 ${timerSecs < 60 && timerRunning ? "bg-red-50" : "bg-slate-100"}`}>
            <Timer className={`w-3 h-3 ${timerSecs < 60 && timerRunning ? "text-red-500" : "text-slate-500"}`} />
            <span className={`font-mono text-xs font-bold ${timerSecs < 60 && timerRunning ? "text-red-600" : "text-slate-700"}`}>{fmtTimer(timerSecs)}</span>
            <Button variant="ghost" size="sm" onClick={() => setTimerRunning(!timerRunning)} className="h-5 px-1 text-[9px]">{timerRunning ? "Pause" : "Start"}</Button>
            <Button variant="ghost" size="sm" onClick={() => { setTimerSecs(subPhase === "team_reveal" ? 7 * 60 * teamMemberCount : 11 * 60); setTimerRunning(false); }} className="h-5 px-1 text-[9px] text-slate-400">Reset</Button>
          </div>

          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg px-1 py-0.5">
            <Button variant="ghost" size="sm" onClick={() => setZoom((z) => Math.max(0.4, z - 0.15))} className="h-5 w-5 p-0"><ZoomOut className="w-3 h-3 text-slate-500" /></Button>
            <span className="text-[9px] font-mono text-slate-500 w-7 text-center">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="sm" onClick={() => setZoom((z) => Math.min(2, z + 0.15))} className="h-5 w-5 p-0"><ZoomIn className="w-3 h-3 text-slate-500" /></Button>
          </div>

          {isOwner && <Button variant="outline" size="sm" data-testid="team-builder-btn" onClick={() => setShowTeamBuilder(true)} className="h-6 text-[10px]"><Settings className="w-3 h-3 mr-0.5" />Teams</Button>}

          <Badge variant="outline" className="text-[9px] font-mono">{displayIdeas.length} ideas</Badge>
          <Button data-testid="add-idea-btn" onClick={() => setShowAdd(true)} className="bg-amber-500 hover:bg-amber-600 text-white h-7 text-[10px] active:scale-[0.98]"><Plus className="w-3 h-3 mr-0.5" />New Idea</Button>

          {subPhase === "consolidated" && (
            <Button data-testid="ai-cluster-btn" onClick={runAIClustering} disabled={clustering} variant="outline" className="h-7 text-[10px] border-violet-300 text-violet-700 hover:bg-violet-50">
              {clustering ? <Loader2 className="w-3 h-3 mr-0.5 animate-spin" /> : <Sparkles className="w-3 h-3 mr-0.5" />}
              {clustering ? "Analyzing..." : clusters.length > 0 ? "Re-cluster" : "AI Group Similar"}
            </Button>
          )}

          {/* Phase controls */}
          {isOwner && subPhase === "individual" && (
            <Button data-testid="reveal-team-btn" onClick={() => advanceSubPhase("team_reveal")} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-[10px]">
              <Eye className="w-3 h-3 mr-0.5" />Reveal to Team
            </Button>
          )}
          {isOwner && subPhase === "team_reveal" && consolidationCountdown === null && (
            <>
              <Button data-testid="announce-consolidation-btn" onClick={() => setShowCountdownSetup(true)} size="sm" variant="outline" className="h-7 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <Bell className="w-3 h-3 mr-0.5" />Announce All-Teams
              </Button>
              <Button data-testid="consolidate-btn" onClick={() => advanceSubPhase("consolidated")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[10px]">
                <Users className="w-3 h-3 mr-0.5" />Consolidate Now
              </Button>
            </>
          )}
          {/* Teams can join early */}
          {!isOwner && subPhase === "team_reveal" && (
            <Button data-testid="join-allteams-btn" onClick={() => advanceSubPhase("consolidated")} size="sm" variant="outline" className="h-7 text-[10px] border-emerald-300 text-emerald-700">
              <ArrowRight className="w-3 h-3 mr-0.5" />Join All-Teams Early
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — always visible */}
        <TeamSidebar
          groups={groups} ideas={displayIdeas} allIdeas={subPhase === "consolidated" ? allIdeas : ideas}
          userId={userId} filterGroup={filterGroup} filterUser={filterUser}
          setFilterGroup={setFilterGroup} setFilterUser={setFilterUser}
          subPhase={subPhase} myGroup={myGroup}
        />

        {/* Canvas */}
        <div className="flex-1 overflow-hidden relative" style={{ background: "repeating-conic-gradient(#f1f5f9 0% 25%, #f8fafc 0% 50%) 0 0 / 30px 30px" }} onContextMenu={(e) => e.preventDefault()} onWheel={handleWheel}>
          <div ref={canvasRef} className="w-full h-full relative" style={{ transform: `scale(${zoom})`, transformOrigin: "0 0" }}>
            {displayIdeas.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Lightbulb className="w-12 h-12 text-amber-200 mx-auto mb-3" />
                  <h3 className="font-heading font-semibold text-lg text-slate-400 mb-1">{subPhase === "individual" ? "Your private board" : "No ideas yet"}</h3>
                  <p className="text-sm text-slate-300">{subPhase === "individual" ? "Add ideas — only you can see them" : "Add sticky notes"}</p>
                </div>
              </div>
            ) : (
              <>
                {/* Render non-merged, non-clustered-hidden ideas */}
                {displayIdeas.filter((i) => !i.is_merged).map((idea, idx) => {
                  const clusterId = ideaClusterMap[idea.id];
                  const cluster = clusterId ? clusters.find((c) => c.id === clusterId) : null;
                  // If clustered, only render the first idea in the cluster as a "stack"
                  if (cluster && cluster.idea_ids[0] !== idea.id) return null;

                  if (cluster) {
                    // Stacked cluster card
                    const clusterIdeas = cluster.idea_ids.map((cid) => displayIdeas.find((i) => i.id === cid)).filter(Boolean);
                    const colors = STICKY_COLORS[clusterIdeas[0]?.category] || STICKY_COLORS.product;
                    const pos = idea.position || { x: 60 + (idx % 5) * 230, y: 20 + Math.floor(idx / 5) * 240 };
                    return (
                      <div key={`cluster-${cluster.id}`} className="absolute cursor-pointer" style={{ left: pos.x, top: pos.y, width: 220, zIndex: 20 }}
                        onClick={() => setShowCluster(cluster)} data-testid={`cluster-stack-${cluster.id}`}>
                        {/* Stacked shadow cards behind */}
                        <div className="absolute top-2 left-2 w-full h-full rounded-sm" style={{ backgroundColor: colors.bg, opacity: 0.5, boxShadow: `2px 3px 8px ${colors.shadow}` }} />
                        <div className="absolute top-1 left-1 w-full h-full rounded-sm" style={{ backgroundColor: colors.bg, opacity: 0.7, boxShadow: `2px 3px 8px ${colors.shadow}` }} />
                        {/* Front card */}
                        <div className="rounded-sm p-3.5 relative" style={{ backgroundColor: colors.bg, borderLeft: `3px solid ${colors.border}`, boxShadow: `2px 3px 12px ${colors.shadow}` }}>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Layers className="w-3.5 h-3.5" style={{ color: colors.text }} />
                            <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: colors.text }}>{cluster.name}</span>
                          </div>
                          <p className="text-xs leading-relaxed" style={{ color: colors.text, opacity: 0.8, fontFamily: "'Caveat', cursive", fontSize: "14px" }}>{cluster.summary}</p>
                          <div className="flex items-center justify-between mt-2 pt-1.5" style={{ borderTop: `1px solid ${colors.border}40` }}>
                            <Badge className="text-[8px] px-1.5 py-0.5" style={{ backgroundColor: colors.border + "50", color: colors.text }}>{clusterIdeas.length} ideas</Badge>
                            <span className="text-[8px]" style={{ color: colors.text, opacity: 0.4 }}>Click to view</span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return <StickyNote key={idea.id} idea={idea} index={idx} userId={userId} zoom={zoom} onDelete={deleteIdea} onSelect={setSelectedIdea} onEdit={startEdit} onDragEnd={handleDragEnd} />;
                })}

                {/* Merged ideas (show with merge indicator) */}
                {displayIdeas.filter((i) => !i.is_merged && i.merged_from?.length > 0).map((idea, idx) => null /* already rendered as regular StickyNote above */)}
              </>
            )}
          </div>

          {/* Cluster legend */}
          {clusters.length > 0 && (
            <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg border border-violet-200 px-3 py-2 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="w-3 h-3 text-violet-500" />
                <span className="text-[10px] font-bold text-violet-700">{clusters.length} AI Clusters</span>
              </div>
              <p className="text-[9px] text-slate-400">Click stacked notes to view &amp; merge</p>
              <Button variant="ghost" size="sm" onClick={() => setClusters([])} className="h-5 text-[9px] text-slate-400 mt-1 px-1">Clear clusters</Button>
            </div>
          )}

          <div className="absolute bottom-3 left-3 text-[9px] text-slate-400 bg-white/80 backdrop-blur-sm rounded-lg px-2 py-1 border border-slate-200">
            {subPhase === "individual" && "Private — only you see these"}{subPhase === "team_reveal" && "Team board"}{subPhase === "consolidated" && "All teams — filter from sidebar"}
            {" · Drag to move · Double-click to open"}
          </div>
        </div>
      </div>

      {/* Countdown Setup Dialog */}
      <Dialog open={showCountdownSetup} onOpenChange={setShowCountdownSetup}>
        <DialogContent className="sm:max-w-sm" data-testid="countdown-setup-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">Announce All-Teams Session</DialogTitle></DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-slate-500">Notify all teams they'll be joining the consolidated board. Set a countdown timer.</p>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-700">Join in</span>
              <Input type="number" min="1" max="30" value={countdownMinutes} onChange={(e) => setCountdownMinutes(parseInt(e.target.value) || 5)} className="h-9 w-20 bg-white text-center font-mono" />
              <span className="text-sm text-slate-700">minutes</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCountdownSetup(false)}>Cancel</Button>
            <Button data-testid="start-countdown-btn" onClick={startConsolidationCountdown} className="bg-indigo-600 hover:bg-indigo-700 text-white"><Bell className="w-4 h-4 mr-1.5" />Announce</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Idea Dialog with sticky note preview */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-3xl" data-testid="add-idea-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">New Sticky Note</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            {/* Form side */}
            <div className="space-y-4">
              <Input data-testid="idea-title-input" placeholder="Idea title" value={newIdea.title} onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })} className="h-11 bg-white text-base" />
              <div className="relative">
                <Textarea data-testid="idea-description-input" placeholder="Describe the idea... or use voice" value={newIdea.description} onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })} className={`min-h-[120px] bg-white text-sm resize-none pr-12 ${recording ? "border-red-300 bg-red-50/30" : ""}`} />
                <Button variant="ghost" size="sm" type="button" data-testid="idea-voice-btn" onClick={toggleVoice} className={`absolute top-2 right-2 h-8 w-8 p-0 ${recording ? "text-red-500 bg-red-50 animate-pulse" : "text-slate-400 hover:text-indigo-600"}`}>{recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</Button>
              </div>
              {recording && <div className="flex items-center gap-2 text-xs text-red-600 -mt-1"><span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />Listening...</div>}
              <Select value={newIdea.category} onValueChange={(v) => setNewIdea({ ...newIdea, category: v })}>
                <SelectTrigger className="h-10 bg-white text-sm"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent><SelectItem value="business_model">Business Model</SelectItem><SelectItem value="product">Product</SelectItem><SelectItem value="service">Service</SelectItem><SelectItem value="process">Process</SelectItem><SelectItem value="region">Region</SelectItem></SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <Select value={newIdea.impact} onValueChange={(v) => setNewIdea({ ...newIdea, impact: v })}>
                  <SelectTrigger className="h-10 bg-white text-sm"><SelectValue placeholder="Impact" /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
                <Select value={newIdea.effort} onValueChange={(v) => setNewIdea({ ...newIdea, effort: v })}>
                  <SelectTrigger className="h-10 bg-white text-sm"><SelectValue placeholder="Effort" /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            {/* Live sticky note preview */}
            <div className="flex items-start justify-center pt-2">
              <div className="w-[210px]" style={{ transform: "rotate(-2deg)" }}>
                {(() => {
                  const previewColors = STICKY_COLORS[newIdea.category] || STICKY_COLORS.product;
                  return (
                    <div className="rounded-sm p-4 transition-colors" style={{
                      backgroundColor: previewColors.bg,
                      borderLeft: `3px solid ${previewColors.border}`,
                      boxShadow: `3px 4px 12px ${previewColors.shadow}, 1px 2px 4px rgba(0,0,0,0.06)`,
                    }}>
                      <span className="inline-block text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mb-2" style={{ backgroundColor: previewColors.border + "40", color: previewColors.text }}>
                        {newIdea.category?.replace("_", " ") || "category"}
                      </span>
                      <h4 style={{ color: previewColors.text, fontFamily: "'Caveat', cursive", fontSize: "18px", minHeight: "24px" }} className="font-semibold leading-tight mb-1">
                        {newIdea.title || "Your idea title..."}
                      </h4>
                      {(newIdea.description || !newIdea.title) && (
                        <p style={{ color: previewColors.text, opacity: newIdea.description ? 0.7 : 0.3, fontFamily: "'Caveat', cursive", fontSize: "14px", lineHeight: "1.3", minHeight: "36px" }}>
                          {newIdea.description || "Description will appear here..."}
                        </p>
                      )}
                      {(newIdea.impact || newIdea.effort) && (
                        <div className="flex gap-1 mt-1">
                          {newIdea.impact && <span className="text-[7px] font-bold uppercase px-1 py-0.5 rounded" style={{ backgroundColor: previewColors.border + "30", color: previewColors.text, opacity: 0.6 }}>{newIdea.impact} impact</span>}
                          {newIdea.effort && <span className="text-[7px] font-bold uppercase px-1 py-0.5 rounded" style={{ backgroundColor: previewColors.border + "30", color: previewColors.text, opacity: 0.6 }}>{newIdea.effort} effort</span>}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2 pt-1.5" style={{ borderTop: `1px solid ${previewColors.border}40` }}>
                        <span className="text-[8px] font-medium" style={{ color: previewColors.text, opacity: 0.5 }}>You</span>
                        <span className="text-[8px]" style={{ color: previewColors.text, opacity: 0.35 }}>Now</span>
                      </div>
                    </div>
                  );
                })()}
                <p className="text-[9px] text-slate-400 text-center mt-2">Live Preview</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button data-testid="submit-idea-btn" onClick={submitIdea} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-white">{submitting ? "Adding..." : "Add Sticky Note"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Idea Dialog */}
      <Dialog open={!!editingIdea} onOpenChange={() => setEditingIdea(null)}>
        <DialogContent className="sm:max-w-3xl" data-testid="edit-idea-dialog">
          {editingIdea && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-lg flex items-center gap-2"><Pencil className="w-4 h-4 text-indigo-500" />Edit Sticky Note</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-6 py-4">
                <div className="space-y-4">
                  <Input data-testid="edit-title-input" placeholder="Idea title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="h-11 bg-white text-base" />
                  <Textarea data-testid="edit-description-input" placeholder="Description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="min-h-[100px] bg-white text-sm resize-none" />
                  <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                    <SelectTrigger className="h-10 bg-white text-sm"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent><SelectItem value="business_model">Business Model</SelectItem><SelectItem value="product">Product</SelectItem><SelectItem value="service">Service</SelectItem><SelectItem value="process">Process</SelectItem><SelectItem value="region">Region</SelectItem></SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={editForm.impact} onValueChange={(v) => setEditForm({ ...editForm, impact: v })}>
                      <SelectTrigger className="h-10 bg-white text-sm"><SelectValue placeholder="Impact" /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low Impact</SelectItem><SelectItem value="medium">Medium Impact</SelectItem><SelectItem value="high">High Impact</SelectItem></SelectContent>
                    </Select>
                    <Select value={editForm.effort} onValueChange={(v) => setEditForm({ ...editForm, effort: v })}>
                      <SelectTrigger className="h-10 bg-white text-sm"><SelectValue placeholder="Effort" /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low Effort</SelectItem><SelectItem value="medium">Medium Effort</SelectItem><SelectItem value="high">High Effort</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-start justify-center pt-2">
                  <div className="w-[210px]" style={{ transform: "rotate(-2deg)" }}>
                    {(() => {
                      const pc = STICKY_COLORS[editForm.category] || STICKY_COLORS.product;
                      return (
                        <div className="rounded-sm p-4" style={{ backgroundColor: pc.bg, borderLeft: `3px solid ${pc.border}`, boxShadow: `3px 4px 12px ${pc.shadow}` }}>
                          <span className="inline-block text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mb-2" style={{ backgroundColor: pc.border + "40", color: pc.text }}>{editForm.category?.replace("_", " ")}</span>
                          <h4 style={{ color: pc.text, fontFamily: "'Caveat', cursive", fontSize: "18px", minHeight: "24px" }} className="font-semibold leading-tight mb-1">{editForm.title || "Title..."}</h4>
                          <p style={{ color: pc.text, opacity: editForm.description ? 0.7 : 0.3, fontFamily: "'Caveat', cursive", fontSize: "14px", lineHeight: "1.3", minHeight: "36px" }}>{editForm.description || "Description..."}</p>
                          {(editForm.impact || editForm.effort) && (
                            <div className="flex gap-1 mt-1">
                              {editForm.impact && <span className="text-[7px] font-bold uppercase px-1 py-0.5 rounded" style={{ backgroundColor: pc.border + "30", color: pc.text, opacity: 0.6 }}>{editForm.impact} impact</span>}
                              {editForm.effort && <span className="text-[7px] font-bold uppercase px-1 py-0.5 rounded" style={{ backgroundColor: pc.border + "30", color: pc.text, opacity: 0.6 }}>{editForm.effort} effort</span>}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <p className="text-[9px] text-slate-400 text-center mt-2">Live Preview</p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingIdea(null)}>Cancel</Button>
                <Button data-testid="save-edit-btn" onClick={saveEdit} className="bg-indigo-600 hover:bg-indigo-700 text-white">Save Changes</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Idea Detail Dialog — full CRUD in all phases including consolidated */}
      <Dialog open={!!selectedIdea} onOpenChange={() => setSelectedIdea(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="idea-detail-dialog">
          {selectedIdea && (<>
            <DialogHeader><DialogTitle className="font-heading text-lg pr-6">{selectedIdea.title}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`text-[10px] border ${BADGE_COLORS[selectedIdea.category] || ""}`}>{selectedIdea.category?.replace("_", " ")}</Badge>
                {selectedIdea.impact && <Badge variant="outline" className="text-[10px]">Impact: {selectedIdea.impact}</Badge>}
                {selectedIdea.effort && <Badge variant="outline" className="text-[10px]">Effort: {selectedIdea.effort}</Badge>}
              </div>
              {selectedIdea.description && <p className="text-sm text-slate-600 leading-relaxed">{selectedIdea.description}</p>}
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span className="flex items-center gap-1"><User className="w-3 h-3" />{selectedIdea.submitted_by_name}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDateTime(selectedIdea.created_at)}</span>
              </div>

              {/* Merged from history */}
              {selectedIdea.merged_from?.length > 0 && (
                <div className="border border-violet-200 bg-violet-50/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-violet-500 flex items-center gap-1"><Layers className="w-3 h-3" />Merged from {selectedIdea.merged_from.length} ideas</span>
                    <Button variant="ghost" size="sm" data-testid="unmerge-btn" onClick={() => unmergeIdea(selectedIdea.id)} className="h-6 text-[10px] text-violet-600 hover:bg-violet-100">
                      <Unlink className="w-3 h-3 mr-0.5" />Unmerge
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {selectedIdea.merged_from.map((orig, oi) => (
                      <div key={oi} className="bg-white rounded-md p-2 border border-violet-100 text-xs">
                        <span className="font-semibold text-slate-700">{orig.title}</span>
                        {orig.description && <p className="text-slate-500 mt-0.5">{orig.description}</p>}
                        <span className="text-[9px] text-slate-400">{orig.submitted_by_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-slate-100 pt-4">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 block"><MessageSquare className="w-3 h-3 inline mr-1" />Notes ({(selectedIdea.notes || []).length})</span>
                <div className="space-y-2 mb-3 max-h-[200px] overflow-y-auto">
                  {(selectedIdea.notes || []).length === 0 && <p className="text-xs text-slate-300 italic">No notes yet</p>}
                  {(selectedIdea.notes || []).map((n) => (
                    <div key={n.id} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <p className="text-sm text-slate-700">{n.text}</p>
                      <div className="flex items-center gap-2 mt-1.5"><span className="text-[10px] text-indigo-600 font-medium">{n.user_name}</span><span className="text-[10px] text-slate-300">{formatDateTime(n.created_at)}</span></div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid="idea-detail-note-input" placeholder="Add a note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && noteText.trim()) addNote(); }} className="h-9 bg-white flex-1 text-sm" />
                  <Button data-testid="idea-detail-add-note-btn" onClick={addNote} disabled={addingNote || !noteText.trim()} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 px-3"><Send className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Team Builder */}
      <TeamBuilder open={showTeamBuilder} onOpenChange={setShowTeamBuilder} boardId={boardId} session={session}
        participants={(session?.pairs || []).flatMap((p) => p.members || []).filter((m, i, a) => a.findIndex((x) => x.user_id === m.user_id) === i)}
        onUpdate={() => { if (onSessionRefresh) onSessionRefresh(); }} />

      {/* Cluster Detail Modal */}
      <Dialog open={!!showCluster} onOpenChange={() => { setShowCluster(null); setDragMergeIds(new Set()); setMergeSummary(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] p-0" data-testid="cluster-detail-dialog">
          {showCluster && (() => {
            const clusterIdeas = (showCluster.idea_ids || []).map((cid) => displayIdeas.find((i) => i.id === cid) || allIdeas.find((i) => i.id === cid)).filter(Boolean);
            return (<>
              <div className="px-6 pt-5 pb-3 border-b border-slate-100">
                <DialogHeader>
                  <DialogTitle className="font-heading text-lg flex items-center gap-2">
                    <Layers className="w-5 h-5 text-violet-500" />
                    {showCluster.name}
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-slate-500 mt-1">{showCluster.summary}</p>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <Badge variant="outline" className="text-[10px]">{clusterIdeas.length} similar ideas</Badge>
                  {dragMergeIds.size >= 2 && (
                    <Button data-testid="merge-selected-btn" onClick={async () => {
                      const result = await mergeSelectedIdeas([...dragMergeIds]);
                      if (result) setMergeSummary({ title: result.title, description: result.description, id: result.id, merged_from: result.merged_from });
                    }} disabled={merging} size="sm" className="bg-violet-600 hover:bg-violet-700 text-white h-7 text-[10px]">
                      {merging ? <Loader2 className="w-3 h-3 mr-0.5 animate-spin" /> : <Sparkles className="w-3 h-3 mr-0.5" />}
                      {merging ? "Merging..." : `Merge ${dragMergeIds.size} Ideas`}
                    </Button>
                  )}
                  <Button onClick={async () => {
                    const result = await mergeSelectedIdeas(showCluster.idea_ids);
                    if (result) setMergeSummary({ title: result.title, description: result.description, id: result.id, merged_from: result.merged_from });
                  }} disabled={merging} variant="outline" size="sm" className="h-7 text-[10px] border-violet-300 text-violet-700">
                    {merging ? "..." : "Merge All"}
                  </Button>
                </div>
                <p className="text-[10px] text-slate-400 mt-2">Click ideas to select for merge. AI will rewrite them into one combined idea.</p>
              </div>

              <ScrollArea className="max-h-[60vh]">
                {/* AI Merged Summary (after merge) */}
                {mergeSummary && (
                  <div className="mx-6 mt-4 border border-violet-300 bg-violet-50 rounded-xl p-5 animate-fade-in">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-violet-600" />
                        <span className="text-xs font-bold uppercase tracking-wider text-violet-600">AI Merged Summary</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button variant="outline" size="sm" data-testid="split-merged-btn" onClick={async () => {
                          await unmergeIdea(mergeSummary.id);
                          setMergeSummary(null);
                          setShowCluster(null);
                        }} className="h-6 text-[10px] border-red-200 text-red-600 hover:bg-red-50">
                          <Unlink className="w-3 h-3 mr-0.5" />Split Back to Original
                        </Button>
                      </div>
                    </div>
                    <Input
                      data-testid="merged-title-input"
                      value={mergeSummary.title}
                      onChange={(e) => setMergeSummary((p) => ({ ...p, title: e.target.value }))}
                      className="h-9 bg-white border-violet-200 font-heading font-semibold text-sm mb-2"
                    />
                    <Textarea
                      data-testid="merged-description-input"
                      value={mergeSummary.description}
                      onChange={(e) => setMergeSummary((p) => ({ ...p, description: e.target.value }))}
                      className="min-h-[80px] bg-white border-violet-200 text-sm resize-none"
                    />
                    <Button size="sm" data-testid="save-merged-edit-btn" onClick={async () => {
                      try {
                        await disruptionAPI.updateIdea(boardId, mergeSummary.id, { title: mergeSummary.title, description: mergeSummary.description, category: "product", impact: "", effort: "" });
                        toast.success("Merged summary updated");
                        fetchIdeas();
                      } catch { toast.error("Failed to save"); }
                    }} className="mt-2 bg-violet-600 hover:bg-violet-700 text-white h-7 text-[10px]">
                      Save Changes
                    </Button>

                    {/* Original ideas preserved */}
                    {mergeSummary.merged_from?.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-violet-200">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-violet-500 mb-2 block">Original Ideas (preserved)</span>
                        <div className="space-y-1.5">
                          {mergeSummary.merged_from.map((orig, oi) => (
                            <div key={oi} className="bg-white rounded-md p-2.5 border border-violet-100 text-xs">
                              <span className="font-semibold text-slate-700">{orig.title}</span>
                              {orig.description && <p className="text-slate-500 mt-0.5">{orig.description}</p>}
                              <span className="text-[9px] text-slate-400">{orig.submitted_by_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Individual sticky notes */}
                {!mergeSummary && (
                  <div className="grid grid-cols-2 gap-3 px-6 py-4">
                    {clusterIdeas.map((idea) => {
                      const colors = STICKY_COLORS[idea.category] || STICKY_COLORS.product;
                      const isSelected = dragMergeIds.has(idea.id);
                      return (
                        <div key={idea.id} data-testid={`cluster-idea-${idea.id}`}
                          onClick={() => setDragMergeIds((prev) => { const n = new Set(prev); n.has(idea.id) ? n.delete(idea.id) : n.add(idea.id); return n; })}
                          className={`rounded-lg p-4 cursor-pointer transition-all relative ${isSelected ? "ring-2 ring-violet-400 ring-offset-2 scale-[1.02]" : "hover:shadow-md"}`}
                          style={{ backgroundColor: colors.bg, borderLeft: `3px solid ${colors.border}` }}>
                          {isSelected && <div className="absolute top-2 right-2 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center"><span className="text-[9px] text-white font-bold">+</span></div>}
                          <span className="inline-block text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mb-1.5" style={{ backgroundColor: colors.border + "40", color: colors.text }}>{idea.category?.replace("_", " ")}</span>
                          <h4 style={{ color: colors.text, fontFamily: "'Caveat', cursive", fontSize: "16px" }} className="font-semibold leading-tight mb-1">{idea.title}</h4>
                          {idea.description && <p className="line-clamp-3 text-xs" style={{ color: colors.text, opacity: 0.7, fontFamily: "'Caveat', cursive" }}>{idea.description}</p>}
                          <div className="flex items-center justify-between mt-2 pt-1.5" style={{ borderTop: `1px solid ${colors.border}40` }}>
                            <span className="text-[8px]" style={{ color: colors.text, opacity: 0.5 }}>{idea.submitted_by_name}</span>
                            <span className="text-[8px]" style={{ color: colors.text, opacity: 0.3 }}>{formatTime(idea.created_at)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </>);
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrainstormView;
